/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import Heading from '@tds/core-heading';
import Modal from '@tds/community-modal';
import InputGroup from '@tds/community-input-group';
import { Link, useLocation } from 'react-router-dom';

import ModalCustom from '../design/shared/modal';
import Table from '../design/shared/table';
import { LinkButton, ContentWrapper, Box } from '../design/css';
import UserForm from './form';
import request, { USER_RIGHTS } from '../utils/request';
import { setConstant } from '../utils/utils';
import ValidateLoader from '../UI/validateLoader';

import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { CONSTANT } from '../utils/constant';
import { BulkUploadBox, Search } from './css';
import {
  getUsersUrl,
  getFormattedPayload,
  isFilterApplied,
  initialFiltersValue
} from './utils';
import MessageNotification from '../messageNotification';
import Filters from './filters';
import ExportReport from './exportReport';

const fetchUsers = url => {
  return request.get(url);
};

const fetchRoles = () => {
  return request.get('/roles');
};

const fetchProvince = () => {
  return request.get('/provinces');
};

const fetchChannels = () => {
  return request.get('/channels');
};

const showBulkUploadButton = () => {
  const { getProfile } = request;
  const profile = getProfile()
    ? USER_RIGHTS[getProfile().role.name]
    : undefined;
  const { isBulkUploadVisible } = profile;
  return isBulkUploadVisible;
};

const Users = ({
  history,
  location: { search },
  match: { params },
  locale
}) => {
  const localizedStrings = useLocalizedStringsContext();
  const location = useLocation();
  const {
    adminLinks: { bulkUpload }
  } = localizedStrings;

  const TRANSLATION = localizedStrings.USERS.LIST;
  const title = TRANSLATION.TITLE;
  const {
    deleteMessage,
    declineMessage,
    approveMessage,
    updateMessage,
    actionButtons: {
      deleteButtonTitle,
      editButtonTitle,
      approveButtonTitle,
      declineButtonTitle
    },
    searchLabelTitle,
    searchHintTitle,
    userProfileModalTitle
    // provincesList
  } = TRANSLATION;
  const tableHeaders = {
    firstName: TRANSLATION.COLUMNS.FIRST_NAME,
    lastName: TRANSLATION.COLUMNS.LAST_NAME,
    email: TRANSLATION.COLUMNS.EMAIL,
    role_description: TRANSLATION.COLUMNS.ROLE,
    actions: TRANSLATION.COLUMNS.ACTIONS
  };
  const ACTIONS = {
    DELETE: TRANSLATION.ACTIONS.DELETE,
    EDIT: TRANSLATION.ACTIONS.EDIT,
    APPROVE: TRANSLATION.ACTIONS.APPROVE,
    DECLINE: TRANSLATION.ACTIONS.DECLINE,
    ENABLE: TRANSLATION.ACTIONS.ENABLE,
    DISABLE: TRANSLATION.ACTIONS.DISABLE
  };
  const { page, q } = queryString.parse(search);
  let { userId } = params;
  const [isLoading, setIsLoading] = useState();
  const [users, setUsers] = useState();
  const focusElementOnClose = useRef(null);
  const [user, setUser] = useState();
  const [actionMessage, setActionMessage] = useState();
  const [roles, setRoles] = useState();
  const [provincesList, setProvincesList] = useState([]);
  const [channels, setChannels] = useState();
  // const [provinces, setProvinces] = useState();
  const [action, setAction] = useState('');
  const [dialogUser, setDialogUser] = useState();
  const [showTelusModal, setShowTelusModal] = useState(false);
  const [searchString, setSearchString] = useState(q);
  const [dealershipList, setDealershipList] = useState([]);
  const [filters, setFilters] = useState(initialFiltersValue);

  const handleUsers = ({ data }) => {
    const tempUsers =
      data &&
      data.users &&
      data.users.map(u => {
        return { ...u, role_description: u.role.description };
      });
    setUsers({ data: tempUsers, count: data.count });
    if (userId) {
      document.body.style.overflow = 'hidden';
      setUser(tempUsers.find(u => u.id === parseInt(userId, 10)));
    }
  };

  useEffect(() => {
    history.push({
      search: `?page=1`
    });
  }, []);

  const fetchFilteredUsers = () =>
    request.post('/users/filter', {
      ...getFormattedPayload(filters),
      page: page || 1
    });

  const getUsers = () => {
    setIsLoading(true);
    const filterApplied = isFilterApplied(filters);
    const dataPromise = filterApplied
      ? fetchFilteredUsers()
      : fetchUsers(getUsersUrl(page, q));

    dataPromise.then(response => {
      handleUsers(response);
      setIsLoading(false);
      window.scrollTo(0, 0);
    });
  };

  const onSearchClick = () => {
    history.push({
      search: `?q=${searchString}`
    });
  };

  const onClear = () => {
    setSearchString('');
    history.push({
      search: ''
    });
  };

  const getDealershipList = list => {
    const updatedList =
      list &&
      list.length &&
      list.map(dealership => {
        return {
          id: dealership.id,
          name: dealership.name,
          description: dealership.name
        };
      });
    return updatedList;
  };

  const getDealerships = async () => {
    request.get(`/dealerships`).then(response => {
      setIsLoading(false);
      if (response && response.status === 200) {
        if (response.data && response.data.rows && response.data.rows.length) {
          setDealershipList(getDealershipList(response.data.rows));
        }
      }
    });
  };

  const restoreUser = async row => {
    request.post(`/users/restore`, { id: row.id }).then(response => {
      setIsLoading(false);
      setActionMessage('User Restored successfully');
      if (response && response.status === 200) {
        getUsers();
      }
    });
  };

  const updateUser = (email, status) => {
    request.put(`/disable-user`, { email, status }).then(response => {
      if (response && response.status === 200) {
        const temp = { ...users };
        for (let i = 0; i < temp.data.length; i += 1) {
          if (temp.data[i].email === email) {
            temp.data[i].isDisabled = temp.data[i].isDisabled === 1 ? 0 : 1;
          }
        }
        setUsers({ data: temp.data, count: temp.data.length });
      }
    });
  };

  // Getting the List of dealership from contentful
  useEffect(() => {
    getDealerships();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchUsers(getUsersUrl(page, q)),
      fetchRoles(),
      fetchProvince(),
      fetchChannels()
    ]).then(([usersResp, rolesResp, provincesResp, channelsResp]) => {
      handleUsers(usersResp);
      const apiRoles = rolesResp.data.roles.filter(item => {
        return (
          !['CORPORATE_STORE_REP', 'CORPORATE_STORE_MANAGER'].includes(
            item.name
          ) && item
        );
      });
      setRoles(apiRoles);
      setConstant('ROLES', apiRoles, 'name');
      setProvincesList(provincesResp.data.provinces);
      setChannels(channelsResp.data.channels);
      // const transformedProvincesList = provResp.data.provinces.map(prov => {
      //   prov.val = prov.name
      //   return prov
      // })
      // setProvinces(transformedProvincesList);
      setConstant('CHANNELS', channelsResp.data.channels, 'name');
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!user) {
      getUsers();
    }
  }, [page, q]);

  const editUser = row => {
    document.body.style.overflow = 'hidden';
    setUser(row);
    history.push(`/${locale}/users/${row.id}`);
  };

  const closeModal = msg => {
    document.body.style.overflow = 'unset';
    setUser();
    setActionMessage(msg);
    setTimeout(() => setActionMessage(null), 3000); // remove update message after 3 seconds
    history.goBack();
    // history.replace({
    //   pathname: `/${locale}/users/`,
    //   search: `?q=${searchString}`
    // });
    // window.location.reload();
  };

  const openTelusModal = () => {
    setShowTelusModal(true);
  };

  const closeTelusModal = () => {
    setShowTelusModal(false);
  };

  const successActionHandling = () => {
    getUsers();
    setDialogUser();
    setAction('');
    closeTelusModal();
  };

  const successfulUpdate = () => {
    userId = undefined;
    closeModal(updateMessage);
    getUsers();
  };

  const approveUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.APPROVE);
    openTelusModal();
  };

  const declineUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.DECLINE);
    openTelusModal();
  };

  const deleteUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.DELETE);
    openTelusModal();
  };

  const onPageChange = pageNumber => {
    history.push(`${location.pathname}?page=${pageNumber}`);
  };

  const proceed = () => {
    switch (action) {
      case ACTIONS.DELETE:
        request.post(`/users/${dialogUser.id}/delete`, dialogUser).then(() => {
          setActionMessage(deleteMessage);
          successActionHandling();
        });
        break;
      case ACTIONS.DECLINE:
        request.post(`/users/${dialogUser.id}/delete`, dialogUser).then(() => {
          setActionMessage(declineMessage);
          successActionHandling();
        });
        break;
      case ACTIONS.APPROVE:
        request.post(`/users/${dialogUser.id}/approve`, dialogUser).then(() => {
          setActionMessage(approveMessage);
          successActionHandling();
        });
        break;
      default:
    }
  };

  const actions = row => {
    if (row.isDeleted) {
      return (
        <>
          User profile is deleted
          <LinkButton onClick={() => restoreUser(row)}>Restore</LinkButton>
        </>
      );
    }
    return (
      <>
        <LinkButton onClick={() => editUser(row)}>{editButtonTitle}</LinkButton>
        |
        {row.isDisabled === 1 ? (
          <LinkButton onClick={() => updateUser(row.email, 0)}>
            {ACTIONS.ENABLE}
          </LinkButton>
        ) : (
          <LinkButton onClick={() => updateUser(row.email, 1)}>
            {ACTIONS.DISABLE}
          </LinkButton>
        )}
        |
        <LinkButton onClick={() => deleteUser(row)}>
          {deleteButtonTitle}
        </LinkButton>
        {row.status === CONSTANT.KEYS.USER_STATUS.PENDING_APPROVAL && (
          <>
            |
            <LinkButton onClick={() => approveUser(row)}>
              {approveButtonTitle}
            </LinkButton>
            |
            <LinkButton onClick={() => declineUser(row)}>
              {declineButtonTitle}
            </LinkButton>
          </>
        )}
      </>
    );
  };
  return (
    <ContentWrapper>
      {isLoading ? (
        <ValidateLoader message="Loading" />
      ) : (
        <>
          {dialogUser && (
            <Modal
              heading={TRANSLATION.confirmationDialog.HEADING}
              bodyText={`${TRANSLATION.confirmationDialog.PRE_TEXT} ${
                TRANSLATION.ACTIONS[action.toUpperCase()]
              } ${dialogUser.firstName} ${dialogUser.lastName} ${
                dialogUser.email
              }?`}
              confirmCTAText={TRANSLATION.confirmationDialog.CONFIRM}
              cancelCTAText={TRANSLATION.confirmationDialog.CANCEL}
              onConfirm={proceed}
              focusElementAfterClose={focusElementOnClose}
              onClose={closeTelusModal}
              isOpen={showTelusModal}
            />
          )}
          <Filters
            filters={filters}
            setFilters={setFilters}
            setUsers={setUsers}
          />
          <Search>
            <BulkUploadBox>
              {showBulkUploadButton() && (
                <Link variant="link" to="/bulk-upload">
                  <span style={{ marginRight: 20 }}>
                    <button type="button" className="frg-button color-purple">
                      {bulkUpload}
                    </button>
                  </span>
                </Link>
              )}
            </BulkUploadBox>
            <InputGroup
              label={searchLabelTitle}
              hint={searchHintTitle}
              defaultValue={searchString}
              onClearButtonClick={() => onClear()}
              onChange={e => setSearchString(e.target.value)}
              onButtonClick={() => onSearchClick()}
              onKeyDown={e => e.key === 'Enter' && onSearchClick()}
            />
          </Search>
          {actionMessage && (
            <Box pt={20} pb={20}>
              <MessageNotification variant="success" message={actionMessage} />
            </Box>
          )}
          {users && users.data && users.data.length === 0 && (
            <Heading level="h3">{TRANSLATION.NO_RECORDS}</Heading>
          )}
          {users && users.data && users.data.length > 0 ? (
            <>
              <Table
                title={title}
                header={tableHeaders}
                data={users.data}
                actions={actions}
                currentPage={parseInt(page || 1, 10)}
                count={users.count}
                url={searchString && `?q=${searchString}`}
                setPage={onPageChange}
              />
              <ExportReport filters={filters} />
            </>
          ) : users && users.data && users.data.length !== 0 ? (
            <Heading level="h3">please wait...</Heading>
          ) : (
            ''
          )}
          {user && roles && channels && (
            <ModalCustom
              open={!!user}
              setOpen={closeModal}
              header={userProfileModalTitle}
            >
              <UserForm
                user={user}
                roles={roles}
                channels={channels}
                provinces={provincesList}
                dealershipList={dealershipList}
                submissionHandling={successfulUpdate}
              />
            </ModalCustom>
          )}
        </>
      )}
    </ContentWrapper>
  );
};

Users.propTypes = {
  locale: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    goBack: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      userId: PropTypes.string
    })
  }).isRequired
};

export default Users;
